import { Injectable, Signal, computed, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs/operators';
import { NebulaActionGate } from 'nebula-ui-kit';

import { AuthorizationStore } from './authorization.store';

/**
 * Adapter del contrato de autorizacion del kit (NEBULA_ACTION_GATE) sobre el
 * AuthorizationStore transversal.
 *
 * El kit pregunta por una llave de binding (alias de instancia o verbo
 * canonico) sin conocer el API; aqui se resuelve contra las acciones que
 * Simappe ya dejo indexadas por segmento de ruta para la opcion activa.
 *
 * Semantica de la senal por llave:
 * - `undefined` mientras el store no termina su carga (la directiva del kit
 *   oculta: evita el destello de botones prohibidos).
 * - `true` solo si la accion viene concedida para la opcion de la ruta.
 * - `false` si viene denegada o NO existe en el mapa de la opcion (modelo
 *   estricto: accion fuera del mapa no aplica a esa pantalla).
 */
@Injectable({ providedIn: 'root' })
export class ActionGateAdapter implements NebulaActionGate {
  private readonly store = inject(AuthorizationStore);
  private readonly router = inject(Router);

  /** URL activa como senal (reacciona a la navegacion). */
  private readonly url = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.router.url),
    ),
    { initialValue: this.router.url },
  );

  /** Senales memoizadas por llave: instancia estable para cada control. */
  private readonly porLlave = new Map<string, Signal<boolean | undefined>>();

  granted(llave: string): Signal<boolean | undefined> {
    let senal = this.porLlave.get(llave);
    if (!senal) {
      senal = computed(() => {
        if (!this.store.loaded()) {
          return undefined;
        }
        return this.store.actionsForRoute(this.url())[llave] === true;
      });
      this.porLlave.set(llave, senal);
    }
    return senal;
  }
}
